/**
 * SettingsLayout — the permanent Settings workspace.
 *
 * Left nav is always visible; only the right pane changes as the URL updates
 * (React Router <Outlet />). A single sticky Save-bar sits at the bottom and
 * only appears when the active section reports unsaved changes.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Search, ChevronRight, Loader2, Save, X } from "lucide-react";

import { REGISTRY, CATEGORIES, findSection } from "./registry";
import { SettingsDirtyProvider, useSettingsDirty } from "./SettingsDirtyContext";

export default function SettingsLayout() {
  return (
    <SettingsDirtyProvider>
      <LayoutInner />
    </SettingsDirtyProvider>
  );
}

function LayoutInner() {
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const loc = useLocation();
  const navigate = useNavigate();
  const dirty = useSettingsDirty();

  // Break the URL into [category, section].
  const { cat, sec, section } = useMemo(() => {
    const parts = loc.pathname.replace(/^\/+|\/+$/g, "").split("/");
    // /app/settings/<cat>/<sec>
    const cat = parts[2];
    const sec = parts[3];
    return { cat, sec, section: findSection(cat, sec) };
  }, [loc.pathname]);

  // Filter registry by search query for the sidebar sub-item filter list.
  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return REGISTRY.filter((r) =>
      `${r.cat} ${r.title} ${r.desc || ""}`.toLowerCase().includes(q),
    ).slice(0, 24);
  }, [query]);

  // `/` focuses search — familiar from Linear / GitHub. Handler doesn't
  // reference any component state, so `[]` deps are correct.
  useEffect(() => {
    const h = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(e.target?.tagName)) {
        e.preventDefault(); searchRef.current?.focus();
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery(""); searchRef.current.blur();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="min-h-full flex" data-testid="settings-layout">
      {/* --------------------- Sidebar --------------------- */}
      <aside className="hidden lg:flex w-[280px] shrink-0 flex-col border-r border-[#EFEADC] bg-[#FAF7EE] sticky top-0 self-start"
             style={{ height: "100vh" }}
             data-testid="settings-sidebar">
        <div className="px-5 pt-6 pb-4">
          <Link to="/app/settings"
                className="font-display text-2xl text-ink tracking-tight block hover:opacity-80">
            Settings
          </Link>
        </div>

        <div className="px-3 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef}
              data-testid="settings-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search settings"
              className="w-full pl-9 pr-8 py-2 rounded-full bg-white border border-[#E7E1D3] text-sm focus:outline-none focus:border-pine focus:ring-2 focus:ring-pine/10" />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-1 py-0.5 rounded border border-pine/20 bg-white text-ink-soft font-mono">/</kbd>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-6">
          {query ? (
            <SearchResults hits={searchHits} onOpen={(r) => {
              navigate(`/app/settings/${r.categorySlug}/${r.sectionSlug}`);
              setQuery("");
            }} />
          ) : (
            <CategoryList activeCat={cat} activeSec={sec} />
          )}
        </nav>
      </aside>

      {/* --------------------- Content --------------------- */}
      <main className="flex-1 min-w-0" data-testid="settings-content">
        <div className="px-5 sm:px-8 lg:px-12 xl:px-16 py-10 pb-32 max-w-[1600px] mx-auto">
          {/* Breadcrumbs */}
          <nav className="text-xs text-ink-soft mb-3 flex items-center gap-1.5 flex-wrap"
               data-testid="breadcrumbs">
            <Link to="/app" className="hover:text-ink">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/app/settings" className="hover:text-ink">Settings</Link>
            {section && (
              <>
                <ChevronRight className="w-3 h-3" />
                <span>{section.cat}</span>
                <ChevronRight className="w-3 h-3" />
                <span className="text-ink font-medium">{section.title}</span>
              </>
            )}
          </nav>

          <Outlet />
        </div>
      </main>

      {/* --------------------- Sticky save bar --------------------- */}
      {dirty.dirty && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:pl-[280px] pointer-events-none"
             data-testid="save-bar">
          <div className="mx-auto max-w-[1600px] pointer-events-auto m-3 lg:m-6 rounded-2xl bg-pine text-linen shadow-[0_20px_50px_-20px_rgba(26,54,43,0.5)] px-4 py-3 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm font-medium">You have unsaved changes</div>
              <div className="text-xs text-linen/60">Save to apply, or discard to revert.</div>
            </div>
            <button
              data-testid="btn-discard"
              onClick={dirty.discard}
              disabled={dirty.saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-linen/25 text-sm hover:bg-linen/10 disabled:opacity-40">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              data-testid="btn-save"
              onClick={dirty.save}
              disabled={dirty.saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-linen text-ink text-sm font-medium hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed">
              {dirty.saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {dirty.saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------------- Sidebar renderers
function CategoryList({ activeCat, activeSec }) {
  return (
    <ul className="space-y-4">
      {CATEGORIES.map((c) => {
        // Hidden sections are ones we haven't released. Filter them from the
        // nav entirely — never expose the tile OR the badge.
        const visible = c.sections.filter((s) => s.status !== "hidden");
        if (visible.length === 0) return null;
        return (
          <li key={c.slug}>
            <div className="overline text-ink-soft px-3 mb-1">{c.label}</div>
            <ul className="space-y-0.5">
              {visible.map((s) => {
                const active = activeCat === c.slug && activeSec === s.slug;
                return (
                  <li key={s.slug}>
                    <NavLink
                      to={`/app/settings/${c.slug}/${s.slug}`}
                      data-testid={`nav-${c.slug}-${s.slug}`}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        active
                          ? "bg-pine/10 text-ink font-medium"
                          : "text-ink-mid hover:bg-white hover:text-ink"
                      }`}>
                      <span className="flex-1 truncate">{s.title}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

function SearchResults({ hits, onOpen }) {
  // Filter hidden results — they should never be searchable either.
  const visible = hits.filter((r) => r.status !== "hidden");
  if (visible.length === 0) {
    return <div className="text-sm text-ink-soft px-3 py-6 text-center">No matches</div>;
  }
  return (
    <ul className="space-y-1">
      {visible.map((r) => (
        <li key={`${r.categorySlug}:${r.sectionSlug}`}>
          <button
            data-testid={`search-hit-${r.sectionSlug}`}
            onClick={() => onOpen(r)}
            className="w-full text-left px-3 py-2 rounded-lg text-sm text-ink hover:bg-white">
            <div className="font-medium truncate">{r.title}</div>
            <div className="text-[11px] text-ink-soft truncate">{r.cat} · {r.desc}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}


// -------------------------------------------------------- Reusable UI helpers
export function SectionHeader({ title, description }) {
  return (
    <header className="mb-8" data-testid="section-header">
      <h1 className="font-display text-4xl text-ink tracking-tight leading-tight">{title}</h1>
      {description && <p className="text-base text-ink-mid mt-3 max-w-2xl leading-relaxed">{description}</p>}
    </header>
  );
}

export function SettingCard({ title, description, children, dataTestId }) {
  return (
    <section className="rounded-2xl bg-white border border-[#EFEADC] p-6 sm:p-8 mb-5"
             data-testid={dataTestId}>
      {(title || description) && (
        <header className="mb-5">
          {title && <div className="font-display text-xl text-ink">{title}</div>}
          {description && <div className="text-sm text-ink-mid mt-1 max-w-2xl leading-relaxed">{description}</div>}
        </header>
      )}
      {children}
    </section>
  );
}
